/**
 * Upload Metrics Service
 * Tracks and persists the last 5 Excel upload metric entries in localStorage.
 */

const STORAGE_KEY = 'nova_upload_metrics';
const MAX_ENTRIES = 10;

export interface FailedRecordDetail {
  rowNumber: number;
  memberId: string;
  message?: string;
}

export interface UploadMetricEntry {
  id: string;
  fileName: string;
  uploadedAt: string; // ISO timestamp
  totalRows: number;
  validRows: number;
  errorRows: number;
  warningRows: number;
  totalRecords: number;
  loadedRecords: number;
  failedRecords: number;
  skippedRecords: number;
  durationMs: number;
  failedDetails?: FailedRecordDetail[];
}

/**
 * Retrieve all stored metric entries (newest first).
 */
export const getUploadMetrics = (): UploadMetricEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UploadMetricEntry[]) : [];
  } catch {
    return [];
  }
};

/**
 * Save a new upload metric entry; trims to the last MAX_ENTRIES.
 */
export const saveUploadMetric = (entry: Omit<UploadMetricEntry, 'id'>): void => {
  try {
    const existing = getUploadMetrics();
    const newEntry: UploadMetricEntry = {
      ...entry,
      id: `metric_${Date.now()}`,
    };
    const updated = [newEntry, ...existing].slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    /* localStorage unavailable */
  }
};

/**
 * Clear all stored metrics.
 */
export const clearUploadMetrics = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
};
